import { useRef } from 'react';
import styled from 'styled-components';
import { GlobalStyle } from '../../../src/fonts/fonts';
import { Arrow } from '../Arrow';
import { AeroportFontCont } from '../../fonts/AeroportFont_Cont.js';
import { Link } from 'react-router-dom';
import gsap from 'gsap';
import { useDispatch } from 'react-redux';
import { InPageHome, NotInPageHome } from '../../redux/InPage';
import { colorRojoNod } from '../../VariablesGlobales';
import SpyGA from '../../Observer.js';

/* --- SHARP MINIMALIST STYLED COMPONENTS --- */

const Underline = styled.div`
  width: 0%;
  height: 1px;
  background-color: black;
`;

const DivA = styled.nav`
  /* Lowered z-index to ensure Burger icon (set to 100+) is clickable */
  z-index: 19;
  display: flex;
  background-color: ${colorRojoNod};
  position: fixed;

  /* Positioned at top right */
  top: 0;
  right: 0;

  /* Animation logic */
  transform: ${({ open }) => (open ? 'translateX(0)' : 'translateX(100%)')};
  transition: transform 0.4s cubic-bezier(0.77, 0, 0.175, 1);

  /* Desktop Dimensions: Sharp Blade */
  height: 25vh;
  width: fit-content;
  min-width: 18vw;
  padding: 0 2vw;
  box-shadow: -10px 0px 30px rgba(0, 0, 0, 0.1);

  @media screen and (max-width: 768px) {
    /* Mobile Dimensions */
    width: 80vw;
    padding: 0 10vw;
    justify-content: center;
    align-items: center;
    /* Keeping it shorter as per your modification, 
       but enough to fit the 45px font items */
    height: 35vh;
  }
`;

const DivABA = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 0; /* Tight link stacking */
  width: 100%;
`;

const DivLinksF = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  cursor: pointer;
`;

const FlechaDiv = styled.div`
  opacity: 0;
  display: flex;
  align-items: center;
  position: absolute;
  /* Brought closer to the text */
  left: -25px;
  pointer-events: none;
`;

/* --- REUSABLE LINK ITEM --- */
const NavLinkItem = ({ children, onClick, to, isExternal = false }) => {
  const arrowRef = useRef(null);
  const textRef = useRef(null);
  const lineRef = useRef(null);

  const handleEnter = () => {
    gsap.to(arrowRef.current, { opacity: 1, x: 5, duration: 0.3 });
    gsap.to(textRef.current, { x: 15, duration: 0.3 });
    gsap.to(lineRef.current, { width: '100%', duration: 0.3 });
  };

  const handleLeave = () => {
    gsap.to(arrowRef.current, { opacity: 0, x: 0, duration: 0.3 });
    gsap.to(textRef.current, { x: 0, duration: 0.3 });
    gsap.to(lineRef.current, { width: '0%', duration: 0.3 });
  };

  const fontConfig = {
    sizePhone: '45px',
    sizeSmall: '30px',
    sizeMedium: '35px',
    sizeBig: '50px',
    lHeightDefault: '0.9',
  };

  return (
    <DivLinksF
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      onClick={onClick}
    >
      <FlechaDiv ref={arrowRef}>
        <Arrow scalePhone="18" scaleBig="18" />
      </FlechaDiv>
      <div ref={textRef} style={{ display: 'flex', flexDirection: 'column' }}>
        <AeroportFontCont {...fontConfig}>
          {isExternal ? (
            <span style={{ color: 'black', whiteSpace: 'nowrap' }}>
              {children}
            </span>
          ) : (
            <Link
              to={to}
              style={{
                textDecoration: 'none',
                color: 'black',
                whiteSpace: 'nowrap',
              }}
            >
              {children}
            </Link>
          )}
          <Underline ref={lineRef} />
        </AeroportFontCont>
      </div>
    </DivLinksF>
  );
};

/* --- MAIN EXPORT --- */
const RightNav = ({ open, setOpen, setIsActive }) => {
  const dispatch = useDispatch();

  const closeAndScroll = () => {
    // 1. Close the Red Nav
    if (setOpen) setOpen(false);
    // 2. Reset the Burger Icon back to lines
    if (setIsActive) setIsActive(false);
    // 3. Move to the top of the page
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <DivA open={open} id="menu">
      <GlobalStyle />
      <DivABA>
        <NavLinkItem
          to="/"
          onClick={() => {
            dispatch(InPageHome());
            SpyGA('Clicked', 'BT_MENU_HOME');
            closeAndScroll();
          }}
        >
          Home.
        </NavLinkItem>

        <NavLinkItem
          to="/About"
          onClick={() => {
            dispatch(NotInPageHome());
            SpyGA('Clicked', 'BT_MENU_ABOUT');
            closeAndScroll();
          }}
        >
          About.
        </NavLinkItem>

        <NavLinkItem
          isExternal
          onClick={() => {
            document
              .getElementById('Footer')
              ?.scrollIntoView({ behavior: 'smooth' });
            SpyGA('Clicked', 'BT_MENU_CONTACT');
            // Close nav so user can see the footer scroll
            if (setOpen) setOpen(false);
            if (setIsActive) setIsActive(false);
          }}
        >
          Contact.
        </NavLinkItem>
      </DivABA>
    </DivA>
  );
};

export default RightNav;
